import type { LicensedProductCategoryName, LicenseTier } from "@packsize/authentication";
import type { HydratedUser } from "@packsize/identity-api-sdk";

export type Permission = {
	name: string;
	text: string;
	license?: {
		productCategory: LicensedProductCategoryName;
		tier: LicenseTier;
		featureFlag?: string;
	};
	featureFlag?: string;
};

// These three permissions arrays have a default list in the controller that must match the values here.
export const allAdministratorPermissions: Permission[] = [
	{
		name: "accessConfigurationAudit",
		text: "Access Configuration Audit",
	},
	{
		name: "accessDashboard",
		text: "Access Dashboard",
		license: { productCategory: "Core", tier: "basic", featureFlag: "ff-pack-net-dashboard" },
	},
	{
		name: "accessOrdersAudit",
		text: "Access Orders Audit",
	},
	{
		name: "accessServiceModePage",
		text: "Access Service Mode Page",
	},
	{
		name: "managePackagingSolutions",
		text: "Manage Packaging Solutions",
		license: {
			productCategory: "Core",
			tier: "basic",
			featureFlag: "ff-pack-net-packaging-creation",
		},
	},
	{
		name: "manageClassifications",
		text: "Manage Classifications",
		license: {
			productCategory: "Core",
			tier: "enterprise",
			featureFlag: "ff-pack-net-classifications",
		},
	},
	{
		name: "manageCorrugates",
		text: "Manage z-Fold",
	},
	{
		name: "manageCPGs",
		text: "Manage Carton Property Groups",
		license: {
			productCategory: "Core",
			tier: "enterprise",
			featureFlag: "ff-pack-net-carton-property-groups",
		},
	},
	{
		name: "manageDesigns",
		text: "Manage Designs",
	},
	{
		name: "manageEventNotifications",
		text: "Manage Event Notifications",
		license: {
			productCategory: "Core",
			tier: "professional",
			featureFlag: "ff-pack-net-event-notifications",
		},
	},
	{
		name: "manageLabelTemplates",
		text: "Manage Label Templates",
		license: {
			productCategory: "Core",
			tier: "basic",
			featureFlag: "ff-pack-net-label-templates",
		},
	},
	{
		name: "manageImportPipelines",
		text: "Manage Import Pipelines",
		license: {
			productCategory: "Core",
			tier: "basic",
			featureFlag: "ff-pack-net-import",
		},
	},
	{
		name: "manageMachines",
		text: "Manage Machines",
	},
	{
		name: "manageMachineGroups",
		text: "Manage Machine Groups",
		license: {
			productCategory: "Core",
			tier: "basic",
			featureFlag: "ff-pack-net-machine-groups",
		},
	},
	{
		name: "manageProductionGroups",
		text: "Manage Production Groups",
		license: {
			productCategory: "Core",
			tier: "basic",
			featureFlag: "ff-pack-net-production-groups",
		},
	},
	{
		name: "ManagePrintImages",
		text: "Manage Print Images",
		license: {
			productCategory: "Core",
			tier: "basic",
			featureFlag: "ff-pack-net-image-templates",
		},
	},

	{
		name: "managePickZones",
		text: "Manage Pick Zones",
		license: {
			productCategory: "Core",
			tier: "enterprise",
			featureFlag: "ff-pack-net-pick-zones",
		},
	},
	{
		name: "managePlannedProduction",
		text: "Manage Planned Production",
	},
	{
		name: "manageRoles",
		text: "Manage Roles",
	},
	{
		name: "manageSettings",
		text: "Manage Settings",
	},
	{
		name: "manageUsers",
		text: "Manage Users",
	},
];

export const allOperatorPermissions: Permission[] = [
	{
		name: "accessMachineControl",
		text: "Access Machine Control",
	},
	{
		name: "changeLoadedCorrugatesOnMachine",
		text: "Change Loaded z-Folds On Machine",
	},
	{
		name: "changeMachineGroupsProductionGroup",
		text: "Move Machine Groups",
	},
	{
		name: "manageJobActions",
		text: "Manage Job Actions",
	},
	{
		name: "modifyAndSendPackagingSolutionToQueue",
		text: "Modify Packaging Solution Before Producing",
		license: {
			productCategory: "Core",
			tier: "basic",
			featureFlag: "ff-pack-net-packaging-creation",
		},
	},
	{
		name: "sendPackagingSolutionToQueue",
		text: "Produce Custom Jobs",
		license: {
			productCategory: "Core",
			tier: "basic",
			featureFlag: "ff-pack-net-packaging-creation",
		},
	},
	{
		name: "createPackagingSolution",
		text: "Create Packaging Solution",
		license: {
			productCategory: "Core",
			tier: "basic",
			featureFlag: "ff-pack-net-packaging-creation",
		},
	},
	{
		name: "createPrintSolution",
		text: "Create Packaging Solution With Print",
		license: {
			productCategory: "Core",
			tier: "enterprise",
			featureFlag: "ff-production-print-manager",
		},
	},
];

export const allReportingPermissions: Permission[] = [
	{
		name: "accessTrimReport",
		text: "Access Trim",
	},
	{
		name: "accessZFoldUsageReport",
		text: "Access zFold Usage",
	},
	{
		name: "accessRawDataReport",
		text: "Access Raw Data",
	},
	{
		name: "accessCartonProductionReport",
		text: "Access Carton Production",
	},
	{
		name: "accessMachineErrorsReport",
		text: "Access Machine Errors and Availability",
	},
	{
		name: "accessProcurementReport",
		text: "Access Procurement",
	},
	{
		name: "AccessOverallEquipmentEffectiveness",
		text: "Access Overall Equipment Effectiveness",
	},
	{
		name: "manageScheduledReports",
		text: "Create and Manage Scheduled Reports",
	},
	{
		name: "manageReportSettings",
		text: "Manage Report Settings",
	},
];

export const scheduledReportPermissions: string[] = [
	"AccessCartonProductionReport",
	"AccessZFoldUsageReport",
	"AccessTrimReport",
	"AccessMachineErrorsReport",
	"AccessOverallEquipmentEffectiveness",
	"manageScheduledReports",
];

export const allDIMPermissions: Permission[] = [
	{
		name: "EditConfiguration",
		text: "Edit Configuration",
	},
	{
		name: "ManageProducts",
		text: "Manage Products (view, create, read, delete)",
	},
	{
		name: "ViewProducts",
		text: "View Products",
	},
	{
		name: "ProduceUsingSingleMode",
		text: "Produce Using Single Mode",
	},
	{
		name: "ProduceUsingMultipleMode",
		text: "Produce Using Multiple Mode",
	},
	{
		name: "ProduceUsingDisto",
		text: "Produce Using Disto",
	},
];

const hasAnyPermission = (userRoles: string[] | undefined, allowedPermissions: string[]): boolean => {
	if (!userRoles) {
		return false;
	}

	const allowed = new Set(allowedPermissions.map((p) => p.toLowerCase()));

	return userRoles.some((r) => allowed.has(r.toLowerCase()));
};

export const hasAnyReportingPermission = (userRoles: string[] | undefined): boolean => {
	return hasAnyPermission(
		userRoles,
		allReportingPermissions.map((p) => p.name),
	);
};

export const hasAnyAdminPermission = (userRoles: string[] | undefined): boolean => {
	return hasAnyPermission(
		userRoles,
		allAdministratorPermissions.map((p) => p.name),
	);
};

export const hasAnyDashboardPermission = (userRoles: string[] | undefined): boolean => {
	return hasAnyPermission(userRoles, ["accessDashboard"]);
};

export const hasAnyOperatorPermission = (userRoles: string[] | undefined): boolean => {
	return hasAnyPermission(
		userRoles,
		allOperatorPermissions.map((p) => p.name),
	);
};

export const hasDashboardRole = (currentUser: HydratedUser | null | undefined): boolean => {
	return hasAnyDashboardPermission(currentUser?.permissions);
};

export const hasReportingRoles = (currentUser: HydratedUser | null | undefined): boolean => {
	return hasAnyReportingPermission(currentUser?.permissions);
};

export const hasConfigurationRoles = (currentUser: HydratedUser | null | undefined): boolean => {
	return hasAnyAdminPermission(currentUser?.permissions);
};

export const hasOperatorRoles = (currentUser: HydratedUser | null | undefined): boolean => {
	return hasAnyOperatorPermission(currentUser?.permissions);
};
